import clsx from "clsx";
import { memo } from "react";

import { symbolSvg } from "../../map/units/symbols";

/** SVG déjà dessinés, par SIDC, taille et numéros : la palette et l'arbre redessinent souvent les mêmes. */
const cache = new Map<string, string>();
const CACHE_MAX = 600;

function svgOf(sidc: string, size: number, left?: string | null, right?: string | null): string {
  const key = `${sidc}|${size}|${left ?? ""}|${right ?? ""}`;
  let svg = cache.get(key);
  if (svg === undefined) {
    svg = symbolSvg(sidc, size, { left, right });
    if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value as string);
    cache.set(key, svg);
  }
  return svg;
}

/** Taille de symbole sous laquelle les numéros dessinés par milsymbol (police à 40 % de la taille) seraient
 *  illisibles : ils passent alors en texte sous le symbole. */
export const AMPLIFIER_MIN_SIZE = 25;

/** Symbole APP-6 d'une unité (SIDC calculé, dessiné par milsymbol, jamais de texte libre) ; `left` et `right` :
 *  numéros en bas à gauche (T) et en bas à droite (M), dessinés comme sur la carte à partir de
 *  `AMPLIFIER_MIN_SIZE`, en texte « T ⁄ M » sous un symbole plus petit. `box` : largeur réservée (px), pour
 *  aligner les lignes d'une liste. */
export const UnitSymbol = memo(function UnitSymbol({
  sidc,
  size = 13,
  left,
  right,
  box,
  className,
}: {
  sidc?: string | null;
  size?: number;
  left?: string | null;
  right?: string | null;
  box?: number;
  className?: string;
}) {
  const caption = size < AMPLIFIER_MIN_SIZE && (left || right);
  if (caption) {
    return (
      <span className={clsx("inline-flex shrink-0 flex-col items-center justify-center gap-0.5", className)} style={box ? { width: box } : undefined} aria-hidden>
        <span className="inline-flex" dangerouslySetInnerHTML={{ __html: sidc ? svgOf(sidc, size) : "" }} />
        <span className="font-mono text-caps leading-none text-ink-muted tabular-nums">
          {left || "–"}
          {right ? ` ⁄ ${right}` : ""}
        </span>
      </span>
    );
  }
  return (
    <span
      className={clsx("inline-flex shrink-0 items-center justify-center", className)}
      style={box ? { width: box } : undefined}
      aria-hidden
      dangerouslySetInnerHTML={{ __html: sidc ? svgOf(sidc, size, left, right) : "" }}
    />
  );
});
